import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Box, Button } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { Close as CloseIcon, ConfirmationNumber as ConfirmationNumberIcon } from '@mui/icons-material';
import { IUserState } from '../../../../Core/State/IUserState';
import { InteractionHandler } from '../../../../Core/Handler/InteractionHandler';
import { ITournamentState } from '../../../../Core/State/ILobbyTournamentState';
import TournamentLobbyContent from './TournamentLobbyContent';
import { canRegisterForTournament, getEligibleTickets, isTournamentRegisteredByUser } from '../../../../Utils/TournamentUtils';

interface DesktopTournamentLobbyModalProps {
    open: boolean;
    onClose: () => void;
    tournament: ITournamentState | null;
    userState: IUserState;
    interactionHandler: InteractionHandler;
}

const DesktopTournamentLobbyModal: React.FC<DesktopTournamentLobbyModalProps> = ({
    open,
    onClose,
    tournament,
    userState,
    interactionHandler
}) => {
    if (!tournament) return null;

    const [actionCooldown, setActionCooldown] = useState(false);
    const eligibleTickets = getEligibleTickets(userState.tickets || [], tournament);
    const isUserRegistered = isTournamentRegisteredByUser(tournament, userState.id);
    const canRegisterNow = canRegisterForTournament(tournament, new Date());
    const hasSpace = tournament.registeredPlayerCount < tournament.maxPlayers;
    const primaryTicket = eligibleTickets[0]?._id;
    const entryMode = tournament.registrationEntryMode || 'cash_only';
    const cashAllowed = entryMode === 'cash_only' || entryMode === 'cash_or_ticket';
    const ticketAllowed = entryMode === 'ticket_only' || entryMode === 'cash_or_ticket';
    const hasEligibleTicket = eligibleTickets.length > 0;
    const showTicketButton = ticketAllowed && hasEligibleTicket;
    const showTicketDisabled = ticketAllowed && !hasEligibleTicket && entryMode === 'ticket_only';

    const runWithCooldown = (action: () => void) => {
        if (actionCooldown) return;

        setActionCooldown(true);
        action();
        setTimeout(() => setActionCooldown(false), 2000);
    };


    const handleRegister = () => {
        runWithCooldown(() => interactionHandler.signUpForTournament(tournament.gameId));
    };

    const handleRegisterWithTicket = () => {
        if (!primaryTicket) return;
        runWithCooldown(() => interactionHandler.registerForTournamentWithTicket(tournament.gameId, primaryTicket));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            maxWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    backgroundColor: '#191A1F',
                    color: 'white',
                    width: '96vw',
                    maxWidth: '1200px',
                    height: '88vh',
                    borderRadius: '6px',
                    margin: '2vh auto',
                },
                '& .MuiDialogContent-root': {
                    padding: 0,
                },
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: '1rem',
                    top: '1rem',
                    color: 'white',
                    p: 0.5,
                    backgroundColor: '#3A3B40',
                    borderRadius: '50% !important',
                    zIndex: 1,
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
            <DialogContent sx={{ p: 0, height: 'calc(88vh - 70px)', overflow: isMobile ? 'auto' : 'hidden', backgroundColor: '#191A1F' }}>
                <TournamentLobbyContent tournament={tournament} userState={userState} interactionHandler={interactionHandler}></TournamentLobbyContent>
            </DialogContent>
        </Dialog>
    );
};

export default DesktopTournamentLobbyModal; 
